import CreditsAPI from 'api/CreditsAPI';
import { getGroupProductDbStats, getGroupStats } from 'api/bitedb/stats/stats.service';
import { ExportHubAPI } from 'api/ExportHubAPI';

export type ProductCreditCheck = {
  ok: boolean;
  used: number;
  total: number;
  remaining: number;
  required: number;
  /** Split of `used`, so the UI can say where the capacity went. */
  breakdown: { entityRows: number; importProducts: number; exportProducts: number };
};

/**
 * Compares a requested row count against the team's remaining combined
 * Database records allowance.
 *
 * The allowance is `db_size + product_db_size` -- the same merged "Database"
 * pool shown on the Subscriptions page and the Super Admin Usage tab (a team
 * still on the legacy split pre-merge has both fields populated; a merged
 * team has everything in db_size and product_db_size at 0 -- summing both
 * covers either state without double-counting). Usage is likewise the sum
 * across all three hubs that draw on it: Entity Hub, Import Hub, and Export
 * Hub, each with its own table, so consumption is the sum of three counts
 * rather than a single query.
 *
 * `insertableRows` must be the count of rows that will actually consume
 * capacity, not a raw parsed row count — callers should exclude rows the import
 * will reject as invalid or duplicate before calling this. Passing the raw
 * count can reject an upload whose net inserted rows would fit (e.g. 91 valid +
 * 10 invalid rows against 100 remaining slots).
 *
 * Throws rather than failing open: a check that silently undercounts would let
 * a team exceed what they have paid for.
 */
export const checkProductCredits = async (
  groupId: string,
  insertableRows: number,
): Promise<ProductCreditCheck> => {
  const [summaryResponse, stats, exportKpis, entityStats] = await Promise.all([
    CreditsAPI.getCreditsSummary(groupId),
    getGroupProductDbStats(groupId),
    ExportHubAPI.getOverviewKpis(groupId),
    getGroupStats(groupId),
  ]);

  if (!summaryResponse?.success || !stats) {
    throw new Error('Unable to validate product database size allowance.');
  }

  const total =
    Number(summaryResponse.result?.db_size ?? 0) +
    Number(summaryResponse.result?.product_db_size ?? 0);
  const entityRows = Number(entityStats?.total_entities ?? 0);
  const importProducts = Number(stats.total_products ?? 0);
  const exportProducts = Number(exportKpis?.total ?? 0);
  const used = entityRows + importProducts + exportProducts;
  const remaining = Math.max(total - used, 0);

  return {
    ok: used + insertableRows <= total,
    used: used,
    total: total,
    remaining: remaining,
    required: insertableRows,
    breakdown: {
      entityRows: entityRows,
      importProducts: importProducts,
      exportProducts: exportProducts,
    },
  };
};
